export type PermissionResourceKind = 'table' | 'column'

/**
 * What the permission grids call each action, by what it's on. The backend
 * keeps its atomic action names: on a table, `read`, `insert`, `update` and
 * `delete` act on its records, `create` makes a field and `drop` deletes the
 * table; on a field, `modify` edits its values and `drop` deletes it.
 */
const PERMISSION_ACTION_LABELS: Record<PermissionResourceKind, Record<string, string>> = {
  table: {
    read: 'View Records',
    insert: 'Add Records',
    update: 'Edit Records',
    delete: 'Delete Records',
    create: 'Create Fields',
    drop: 'Delete Table',
    manage: 'Manage Table Settings',
  },
  column: {
    read: 'View Field Values',
    modify: 'Edit Field Values',
    drop: 'Delete Field',
    manage: 'Manage Field Settings',
  },
}

/** A table's record permissions, in the grids' order. Row filters will scope these. */
export const TABLE_RECORD_ACTION_NAMES = ['read', 'insert', 'update', 'delete'] as const

/** A table's own permissions (its fields and the table itself), in the grids' order. */
export const TABLE_STRUCTURE_ACTION_NAMES = ['create', 'drop', 'manage'] as const

/** Every table permission the grids show. */
export const TABLE_ACTION_NAMES = [...TABLE_RECORD_ACTION_NAMES, ...TABLE_STRUCTURE_ACTION_NAMES]

/** A field's permissions, in the grids' order. */
export const FIELD_ACTION_NAMES = ['read', 'modify', 'drop', 'manage'] as const

export function getPermissionActionLabel(
  resourceKind: PermissionResourceKind,
  actionName: string
): string {
  return PERMISSION_ACTION_LABELS[resourceKind][actionName] ?? actionName
}

/** The actions with their labels, for a grid's columns. */
export function toPermissionActions(
  resourceKind: PermissionResourceKind,
  actionNames: readonly string[]
): { action: string; label: string }[] {
  return actionNames.map((action) => ({
    action,
    label: getPermissionActionLabel(resourceKind, action),
  }))
}
